/**
 * datos-iniciales.js — Autogestión de datos al primer inicio de sesión.
 *
 * A los colaboradores dados de alta con el "alta rápida" (Administración →
 * Alta rápida) solo se les carga nombre, cédula y correo: quedan en estado
 * "preregistro". Este gate les pide lo que falta la primera vez que entran y,
 * cuando ya no falta nada, los deja "activo" — lo que dispara la asignación de
 * cursos obligatorios y el correo de bienvenida, igual que si los activara un
 * administrador desde el panel.
 *
 * Es un espejo del candado del backend (app/core/auth.py::_block_if_must_change):
 * aunque el usuario esquive esta pantalla, la API le seguirá respondiendo 403.
 * La pantalla existe para que sepa QUÉ hacer, no para proteger nada.
 *
 * Ojo con el nombre: `window.Onboarding` ya está tomado por el tour de
 * bienvenida (onboarding.js), que es opcional y decorativo. Esto es otra cosa.
 */
const DatosIniciales = (() => {

  /**
   * gate() — Promise que resuelve cuando el colaborador ya puede usar el portal.
   * Resuelve `true` para continuar y `false` si cerró sesión sin completar.
   * Si el endpoint falla NO se bloquea el portal: el candado real está en el
   * backend, y dejar al usuario encerrado por un error de red sería peor.
   */
  async function gate() {
    const user = (typeof Auth !== 'undefined') ? Auth.getUser() : null;
    // Atajo: si el login ya dijo que está activo, ni siquiera consultamos.
    if (user && user.status && user.status !== 'preregistro') return true;

    let data;
    try {
      data = await API.get('/auth/onboarding');
    } catch {
      return true;
    }
    if (!data?.bloqueado) return true;

    return new Promise((resolve) => _render(data, resolve));
  }

  // ── Formulario generado desde el esquema de la ficha ─────────────────────
  // Los campos, sus tipos y sus opciones vienen del servidor: si Bienestar
  // agrega una columna a la Base de Datos Empleados, aparece aquí sola.
  //
  // El renderizador es a propósito un SUBCONJUNTO del de page-private-records:
  // solo los tipos que un colaborador puede escribir. Los complejos —empleado
  // con autocompletar, fórmulas, relaciones— corresponden a campos que él nunca
  // edita, así que no hacen falta. Y esta pantalla es un candado previo al
  // arranque de la aplicación: no puede depender de un módulo de administración.
  const _TIPO_INPUT = { number: 'number', date: 'date', email: 'email',
                        phone: 'tel', text: 'text' };

  function _campoHtml(f, valor, idValor) {
    const id = `di-f-${f.key}`;
    const val = Utils.escapeHtml(valor == null ? '' : String(valor));
    const req = f.required ? ' *' : '';
    // Bloqueado: se MUESTRA igual, con su valor y el motivo. Verlo tiene valor
    // por sí mismo — hoy nadie detecta que su área o su cargo están mal.
    if (!f.editable) {
      return `<div class="form-group">
        <label class="form-label" for="${id}">${Utils.escapeHtml(f.label)}${f.sensible ? ' <i class="bi bi-shield-exclamation" title="Dato sensible"></i>' : ''}
          <span class="di-opt">(${Utils.escapeHtml(f.motivo || 'no editable')})</span></label>
        <input class="form-input" id="${id}" value="${val}" disabled
               style="background:var(--gray-100);color:var(--gray-700);cursor:not-allowed"></div>`;
    }
    let inner;
    if (f.type === 'textarea') {
      inner = `<textarea class="form-textarea" id="${id}" rows="2">${val}</textarea>`;
    } else if (f.type === 'employee') {
      // Columna de tipo Empleado que el colaborador sí llena (p. ej. "padrino de
      // inducción"). Antes caía al `else` y se pintaba como caja de texto plana, con
      // lo que el dato se guardaba como texto libre.
      inner = Utils.campoEmpleadoHtml(id, valor, idValor);
    } else if (f.type === 'select') {
      inner = `<select class="form-select" id="${id}"><option value="">— Selecciona —</option>` +
        (f.options || []).map(o => `<option value="${Utils.escapeHtml(o)}" ${
          String(valor) === String(o) ? 'selected' : ''}>${Utils.escapeHtml(o)}</option>`).join('') +
        `</select>`;
    } else {
      const t = _TIPO_INPUT[f.type] || 'text';
      const max = t === 'date' && f.key === 'fecha_nacimiento' ? ` max="${_maxFecha()}"` : '';
      inner = `<input type="${t}" class="form-input" id="${id}" value="${val}"${max}>`;
    }
    // `aviso` lo manda el servidor cuando abre un campo solo para poder desbloquear
    // la cuenta (hoy: la fecha de nacimiento cerrada a la autogestión).
    const ayuda = f.aviso
      ? `<p class="di-hint">${Utils.escapeHtml(f.aviso)}</p>`
      : (f.key === 'fecha_nacimiento'
         ? '<p class="di-hint">La usamos para felicitarte en tu cumpleaños.</p>' : '');
    return `<div class="form-group">
      <label class="form-label" for="${id}">${Utils.escapeHtml(f.label)}${req}</label>
      ${inner}${ayuda}</div>`;
  }

  function _camposHtml(data) {
    const campos = data.campos || [];
    const ficha = data.ficha || {};
    if (!campos.length) {
      // Sin esquema (servidor antiguo): al menos la fecha, que es la que activa.
      return `<div class="form-group">
        <label class="form-label" for="di-f-fecha_nacimiento">Fecha de nacimiento *</label>
        <input type="date" class="form-input" id="di-f-fecha_nacimiento"
               value="${(data.datos || {}).birth_date || ''}" max="${_maxFecha()}">
        <p class="di-hint">La usamos para felicitarte en tu cumpleaños.</p></div>`;
    }
    // Lo que el colaborador llena va PRIMERO; lo que solo puede consultar, al
    // final y plegado, para que no parezca que tiene treinta campos por delante.
    const mios = campos.filter(f => f.editable);
    const ajenos = campos.filter(f => !f.editable);
    return mios.map(f => _campoHtml(f, ficha[f.key], ficha[f.key + '_id'])).join('') +
      (ajenos.length ? `<details class="di-ajenos">
        <summary>Ver mis datos laborales (${ajenos.length})</summary>
        <p class="di-hint">Los administra Gestión Humana. Si algo está mal, avísale a Bienestar.</p>
        ${ajenos.map(f => _campoHtml(f, ficha[f.key], ficha[f.key + '_id'])).join('')}
      </details>` : '');
  }

  function _render(data, resolve) {
    document.getElementById('datos-overlay')?.remove();
    const faltan = data.faltantes || [];
    const pideCargo = faltan.includes('position') && (data.cargos || []).length > 0;
    const d = data.datos || {};
    const nombre = (d.full_name || '').split(' ')[0] || '';

    const ov = document.createElement('div');
    ov.id = 'datos-overlay';
    ov.className = 'legal-overlay';
    ov.innerHTML = `
      <div class="legal-modal">
        <div class="legal-head">
          <i class="bi bi-person-vcard"></i>
          <div>
            <h2>${nombre ? `¡Bienvenido, ${Utils.escapeHtml(nombre)}!` : '¡Bienvenido!'}</h2>
            <p>Para terminar de activar tu cuenta necesitamos unos datos.
               Después podrás consultarlos y actualizarlos en tu perfil,
               en <strong>Mi ficha</strong>.</p>
          </div>
        </div>
        <div class="legal-docs">
          ${_camposHtml(data)}
          ${pideCargo ? `
          <div class="form-group">
            <label class="form-label" for="di-cargo">Cargo *</label>
            <select class="form-select" id="di-cargo">
              <option value="">Selecciona tu cargo…</option>
              ${data.cargos.map(c =>
                `<option value="${c.id}">${Utils.escapeHtml(c.name)}</option>`).join('')}
            </select>
          </div>` : ''}
          <p class="di-error" id="di-error" hidden></p>
        </div>
        <div class="legal-foot">
          <button class="btn btn-ghost" id="di-logout">Cerrar sesión</button>
          <button class="btn btn-primary" id="di-save">
            <i class="bi bi-check2-circle"></i> Guardar y continuar
          </button>
        </div>
      </div>`;
    document.body.appendChild(ov);
    // El autocompletar necesita el DOM ya insertado.
    (data.campos || []).filter(f => f.editable && f.type === 'employee')
      .forEach(f => Utils.campoEmpleadoWire(`di-f-${f.key}`, f.key));

    const $ = (id) => document.getElementById(id);
    const err = $('di-error');
    const btn = $('di-save');

    function mostrarError(msg) {
      err.textContent = msg;
      err.hidden = false;
    }

    $('di-logout').addEventListener('click', () => {
      ov.remove();
      resolve(false);
      if (typeof Auth !== 'undefined') Auth.logout();
    });

    btn.addEventListener('click', async () => {
      err.hidden = true;
      // Se recoge TODO lo editable del esquema. El servidor vuelve a
      // filtrar por la misma marca: aquí solo se evita un viaje.
      const ficha = {};
      (data.campos || []).filter(f => f.editable).forEach(f => {
        const el = $(`di-f-${f.key}`);
        if (el) ficha[f.key] = (el.value || '').trim();
        if (f.type === 'employee') {
          // El id del colaborador elegido: sin él, dos homónimos son indistinguibles.
          const oculto = $(`di-f-${f.key}-id`);
          if (oculto?.value) ficha[`${f.key}_id`] = oculto.value;
        }
      });
      const birth = ficha.fecha_nacimiento || $('di-f-fecha_nacimiento')?.value || '';
      if (!birth) return mostrarError('Ingresa tu fecha de nacimiento.');
      // Los demás obligatorios del esquema, con el nombre del campo.
      const falta = (data.campos || []).find(f => f.editable && f.required && !ficha[f.key]);
      if (falta) return mostrarError(`Completa el campo obligatorio: ${falta.label}.`);
      if (pideCargo && !$('di-cargo').value) return mostrarError('Selecciona tu cargo.');

      btn.disabled = true;
      const original = btn.innerHTML;
      btn.innerHTML = '<i class="bi bi-hourglass-split"></i> Guardando…';
      try {
        const body = { birth_date: birth, ficha };
        if (pideCargo) body.position_id = parseInt($('di-cargo').value, 10);
        const res = await API.post('/auth/onboarding', body);

        // El usuario en localStorage trae el status del login; si no se refresca
        // aquí, el gate volvería a dispararse en la siguiente navegación.
        if (typeof Auth !== 'undefined') Auth.setUserStatus(res.status || 'activo');

        ov.classList.add('legal-closing');
        setTimeout(() => ov.remove(), 250);
        Utils.toast(res.message || '¡Listo! Tus datos quedaron registrados.', 'success');
        resolve(true);
      } catch (e) {
        mostrarError(e.message || 'No se pudieron guardar los datos.');
        btn.disabled = false;
        btn.innerHTML = original;
      }
    });
  }

  /** Tope del date picker: hoy menos 15 años (edad mínima que valida el backend). */
  function _maxFecha() {
    const h = new Date();
    return new Date(h.getFullYear() - 15, h.getMonth(), h.getDate())
      .toISOString().slice(0, 10);
  }

  return { gate };
})();
window.DatosIniciales = DatosIniciales;
